import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Home, LogOut, LayoutDashboard, Bell, ArrowLeft, Store, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/notifications/NotificationBell";
import { useMiniSiteContext } from "@/lib/minisiteContext";
import { usePublicSiteCms } from "@/lib/siteCms";

const publicNav = [
  { to: "/", label: "Beranda", testid: "nav-home" },
  { to: "/search", label: "Cari Hunian", testid: "nav-search" },
];

const roleLabel = { customer: "Customer", owner: "Owner", admin: "Admin" };

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const minisite = useMiniSiteContext();
  const cms = usePublicSiteCms();

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  // Web-site mode: visitor arrived via an owner storefront. Show a minimal
  // storefront header (owner identity + back to web-site) instead of the AVASA
  // master nav, so they stay in the owner's context.
  if (minisite && minisite.slug) {
    return (
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-background/90 border-b border-border/60"
        data-testid="minisite-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-3">
            <Link to={`/properti/${minisite.slug}`} className="flex items-center gap-2.5 min-w-0 group" data-testid="minisite-brand">
              {minisite.logo_url ? (
                <img src={minisite.logo_url} alt={minisite.owner_name || "Web-site"}
                  className="w-9 h-9 rounded-xl object-cover border border-border shrink-0" />
              ) : (
                <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary text-primary-foreground shrink-0">
                  <Store size={18} />
                </span>
              )}
              <span className="font-heading text-base sm:text-lg font-bold tracking-tight truncate">
                {minisite.owner_name || "Web-Site"}
              </span>
            </Link>
            <Link
              to={`/properti/${minisite.slug}`}
              data-testid="minisite-navbar-back"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-secondary text-primary hover:scale-105 active:scale-95 transition-transform shrink-0"
            >
              <ArrowLeft size={15} /> <span className="hidden sm:inline">Kembali ke web-site</span><span className="sm:hidden">Web-site</span>
            </Link>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/60"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
            {cms?.branding?.logo_url ? (
              <img src={cms.branding.logo_url} alt={cms.branding.brand_name || "AVASA"}
                className="w-9 h-9 rounded-xl object-cover transition-transform group-hover:scale-105" />
            ) : (
              <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary text-primary-foreground transition-transform group-hover:scale-105">
                <Home size={18} />
              </span>
            )}
            <span className="font-heading text-xl font-bold tracking-tight text-primary">{cms?.branding?.brand_name || "AVASA"}</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {publicNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:text-primary hover:bg-secondary/60"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {user && (
            <div className="flex items-center ml-auto mr-1 md:ml-0 md:mr-0" data-testid="navbar-bell-wrap">
              <NotificationBell />
            </div>
          )}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <button
                  onClick={() => navigate(`/${user.role}`)}
                  data-testid="navbar-dashboard-button"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors"
                >
                  <LayoutDashboard size={16} /> Dashboard
                </button>
                <button
                  onClick={() => navigate("/pengaturan-akun")}
                  data-testid="navbar-security-button"
                  title="Keamanan Akun"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors"
                >
                  <Settings size={16} />
                </button>
                <span className="text-sm text-muted-foreground" data-testid="navbar-user-name">
                  {user.name} · <span className="text-primary font-semibold">{roleLabel[user.role]}</span>
                </span>
                <button
                  onClick={handleLogout}
                  data-testid="navbar-logout-button"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-secondary text-primary hover:scale-105 active:scale-95 transition-transform"
                >
                  <LogOut size={16} /> Keluar
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  data-testid="navbar-login-button"
                  className="px-4 py-2 rounded-full text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors"
                >
                  Masuk
                </button>
                <button
                  onClick={() => navigate("/register")}
                  data-testid="navbar-register-button"
                  className="px-5 py-2 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-105 active:scale-95 transition-transform"
                >
                  Daftar
                </button>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button
              className="p-2 rounded-lg text-primary hover:bg-secondary/60 transition-colors"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border/60 bg-background" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {publicNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/60"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {user ? (
              <div className="space-y-2 pt-2">
                <button
                  onClick={() => { setOpen(false); navigate(`/${user.role}`); }}
                  className="w-full px-4 py-2.5 rounded-full text-sm font-semibold border border-border text-primary"
                  data-testid="mobile-dashboard-button"
                >
                  Dashboard ({roleLabel[user.role]})
                </button>
                <button
                  onClick={() => { setOpen(false); navigate("/pengaturan-akun"); }}
                  className="w-full px-4 py-2.5 rounded-full text-sm font-semibold border border-border text-primary inline-flex items-center justify-center gap-2"
                  data-testid="mobile-security-button"
                >
                  <Settings size={15} /> Keamanan Akun
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2.5 rounded-full text-sm font-semibold bg-secondary text-primary"
                  data-testid="mobile-logout-button"
                >
                  Keluar
                </button>
              </div>
            ) : (
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { setOpen(false); navigate("/login"); }}
                  className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold border border-border text-primary"
                  data-testid="mobile-login-button"
                >
                  Masuk
                </button>
                <button
                  onClick={() => { setOpen(false); navigate("/register"); }}
                  className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold bg-accent text-accent-foreground"
                  data-testid="mobile-register-button"
                >
                  Daftar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
